// areas/settings/survey-question/parts/SurveyQuestionList.tsx
import React, { useState, useMemo } from 'react';
import { Dropdown, Icon, Label, Pagination, Modal, Form, Button } from 'semantic-ui-react';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { PageSection } from 'common/styles/page';
import { StatusEd2Enum, surveyQuestionApi } from '../api/surveyQuestionApi';
import { SurveyQuestionRead } from 'store/generated/admin/survey_question';
import { QuestionStatus, QuestionTypes } from '../constants/SurveyQuestionTypes';
import styled from 'styled-components';

interface SurveyQuestionListProps {
  questions: SurveyQuestionRead[];
  currentPage: number;
  onPageChange: (page: number) => void;
  onNavigate: (path: string) => void;
  refetch: () => void;
}

const PAGE_SIZE = 9;

const surveyTypeLabel = (t?: number) => {
  const map: Record<number, string> = {
    [QuestionTypes.PILLARS]: 'Pillars',
    [QuestionTypes.OUTCOMES]: 'Outcomes',
  };
  return t ? map[t] ?? `Type ${t}` : '—';
};

const statusIcon = (s?: number) => {
  if (s === QuestionStatus.ARCHIVED) return { name: 'archive', color: 'red', label: 'Archived' };
  return { name: 'check circle', color: 'green', label: 'Active' };
};

const SurveyQuestionList: React.FC<SurveyQuestionListProps> = ({
  questions,
  currentPage,
  onPageChange,
  onNavigate,
  refetch,
}) => {
  const paged = useMemo(() => {
    const sorted = [...questions].sort((a, b) =>
      String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' })
    );
    const start = (currentPage - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [questions, currentPage]);

  const [patch] = surveyQuestionApi.useApiAdminV1SurveyQuestionUpdateMutation();
  const [destroyQuestion] = surveyQuestionApi.useApiAdminV1SurveyQuestionDestroyMutation();
  const [duplicate, { isLoading: duplicating }] =
    surveyQuestionApi.useApiAdminV1SurveyQuestionDuplicateCreateMutation();

  // duplicate modal
  const [dupOpen, setDupOpen] = useState(false);
  const [dupName, setDupName] = useState('');
  const [dupTarget, setDupTarget] = useState<SurveyQuestionRead | null>(null);
  const canSaveDup = dupName.trim().length > 0;

  const openDuplicateModal = (q: SurveyQuestionRead, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDupTarget(q);
    setDupName(`${q.name} (Copy)`);
    setDupOpen(true);
  };
  const closeDuplicateModal = () => {
    setDupOpen(false);
    setDupTarget(null);
    setDupName('');
  };
  const confirmDuplicate = async () => {
    if (!dupTarget || !canSaveDup) return;
    try {
      await duplicate({
        id: dupTarget.id,
        surveyQuestionDuplicate: { name: dupName.trim() },
      }).unwrap();
      closeDuplicateModal();
      refetch();
    } catch { /* noop */ }
  };

  // status + delete helpers
  const setStatus = async (q: SurveyQuestionRead, s: StatusEd2Enum, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await patch({
        id: q.id,
        surveyQuestion: {
          name: q.name,
          sort_order: q.sort_order,
          status: s,
          survey_type: q.survey_type,
          url_location: q.url_location,
          questions: q.questions,
        },
      }).unwrap();
      refetch();
    } catch { /* noop */ }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <HeaderCell width={4}>Name</HeaderCell>
            <HeaderCell width={4}>Type</HeaderCell>
            <HeaderCell width={3}>Status</HeaderCell>
            <HeaderCell width={2} justify="center">Actions</HeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paged.map((q) => {
            const s = statusIcon(q.status);
            return (
              <TableRow
                key={q.id}
                $selectable
                onClick={() => onNavigate(`/settings/admin/survey-questions/${q.id}/edit`)}
              >
                <TableCell $firstcol>{q.name}</TableCell>
                <TableCell>
                  <Label color={q.survey_type === QuestionTypes.PILLARS ? 'purple' : q.survey_type === QuestionTypes.OUTCOMES ? 'teal' : 'blue'}>
                    {surveyTypeLabel(q.survey_type)}
                  </Label>
                </TableCell>
                <TableCell>
                  <ValidationIcon>
                    <Icon name={s.name as any} color={s.color as any} size="large" title={s.label} />
                    <span>{s.label}</span>
                  </ValidationIcon>
                </TableCell>

                <TableCell justify="center">
                  <Dropdown
                    button
                    icon="ellipsis vertical"
                    floating
                    className="icon"
                    direction="left"
                    style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Dropdown.Menu>
                      <Dropdown.Item
                        icon="edit"
                        text="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigate(`/settings/admin/survey-questions/${q.id}/edit`);
                        }}
                      />
                      <Dropdown.Item
                        icon="copy"
                        text="Duplicate"
                        onClick={(e) => openDuplicateModal(q, e)}
                      />

                      {q.status === QuestionStatus.ACTIVE && (
                        <Dropdown.Item
                          icon="archive"
                          text="Archive"
                          onClick={(e) => setStatus(q, QuestionStatus.ARCHIVED as StatusEd2Enum, e)}
                        />
                      )}

                      {q.status === QuestionStatus.ARCHIVED && (
                        <Dropdown.Item
                          icon="undo"
                          text="Unarchive"
                          onClick={(e) => setStatus(q, QuestionStatus.ACTIVE as StatusEd2Enum, e)}
                        />
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <PageSection display="flex" justifyContent="end">
        <Pagination
          activePage={currentPage}
          totalPages={Math.ceil(questions.length / PAGE_SIZE)}
          onPageChange={(_, data) => onPageChange(data.activePage as number)}
          firstItem={null}
          lastItem={null}
          prevItem={{ content: '‹', icon: true }}
          nextItem={{ content: '›', icon: true }}
        />
      </PageSection>

      {/* Duplicate modal */}
      <Modal open={dupOpen} onClose={closeDuplicateModal} size="small">
        <Modal.Header>Duplicate Question Set</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              autoFocus
              label="New Question Set Name"
              value={dupName}
              onChange={(_, { value }) => setDupName(String(value))}
              placeholder="Enter a new name for the copy"
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeDuplicateModal} disabled={duplicating}>Cancel</Button>
          <Button
            primary
            icon="copy"
            content="Create Copy"
            onClick={confirmDuplicate}
            disabled={!canSaveDup || duplicating}
            loading={duplicating}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default SurveyQuestionList;

const ValidationIcon = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;

  .ui.icon {
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  span {
    display: inline-flex;
    align-items: center;
  }
`;
